// ActivitySection — 「活動記錄」 audit log (W3.1 §5.2 + §3.1 AnniversaryActivity).
//
// Renders append-only activity entries with actor + zh-Hant action label +
// relative timestamp. Backend already returns label_zh localized.
//
// W52.1 D-5 — this was the plainest of the three streams: a bare `<ul>` with
// an `h3` above it, no card, no count, no way to see anything past what had
// been paged in. It now renders the same `ActivityStreamCard` as the wish
// timeline and the dashboard.
//
// **What did not change, deliberately: 「載入更多」.** That control answers
// "how much is left to *fetch*"; the collapse answers "how much of what I have
// do I *show*". They are different questions, and this stream is the one place
// both are real — `useInfiniteQuery` means the list genuinely has more behind
// it. Conflating them would leave the section as long as before whenever the
// user happened to have paged a lot, which is the exact complaint the collapse
// was meant to address.

import { useMemo, useState } from "react";
import { useAnniversaryActivity } from "@/lib/hooks/anniversary";
import { LoadingState } from "@/components/primitives/LoadingState";
import { EmptyState } from "@/components/primitives/EmptyState";
import {
  ACTIVITY_COLLAPSED_LIMIT,
  ActivityStreamCard,
} from "@/components/activity/ActivityStreamCard";
import { formatRelative } from "@/lib/utils/date";

export interface ActivitySectionProps {
  anniversaryId: string;
}

export function ActivitySection({ anniversaryId }: ActivitySectionProps) {
  const query = useAnniversaryActivity(anniversaryId);
  const [expanded, setExpanded] = useState(false);
  // `useInfiniteQuery`: the audit log is append-only and grows without bound, so
  // page 1 alone is a silent truncation — and a truncation that looks complete
  // is worse than an obvious one.
  const loaded = useMemo(
    () => (query.data?.pages ?? []).flatMap((p) => p.items),
    [query.data],
  );
  // The **total** for the badge is everything fetched so far. This endpoint
  // reports no grand total across all pages, so the badge deliberately counts
  // what is loaded rather than inventing a number the client cannot know.
  const total = loaded.length;
  // Collapse is a client-side slice — unlike the wish timeline, which re-queries.
  const visible = expanded ? loaded : loaded.slice(0, ACTIVITY_COLLAPSED_LIMIT);

  return (
    <ActivityStreamCard
      title="活動記錄"
      total={total}
      visibleCount={loaded.length}
      expanded={expanded}
      onToggleExpanded={() => setExpanded((v) => !v)}
      collapsible={!query.isLoading}
      testId="activity-section"
    >
      <div className="flex flex-col gap-3">
        {query.isLoading ? (
          <LoadingState count={2} shape="row" />
        ) : loaded.length === 0 ? (
          <EmptyState title="暫無活動記錄" description="這個紀念日還沒有任何操作紀錄" />
        ) : (
          <ul className="flex flex-col gap-2">
            {visible.map((a) => (
              <li
                key={a.id}
                className="flex items-start gap-3 text-body-sm text-ink-1"
                data-testid={`activity-${a.id}`}
              >
                <span
                  aria-hidden
                  className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-paper-tint1 text-ink-2 text-meta"
                >
                  {a.actor?.display_name?.slice(0, 1) ?? "·"}
                </span>
                <p className="flex-1 min-w-0">
                  <span className="font-semibold">{a.actor?.display_name ?? "系統"}</span>
                  <span className="text-ink-2 ml-1">{a.label_zh}</span>
                </p>
                <span className="text-meta text-ink-3 shrink-0 tabular-nums">
                  {formatRelative(new Date(a.created_at))}
                </span>
              </li>
            ))}
          </ul>
        )}
        {/* Pagination, not collapse — see the file header. */}
        {query.hasNextPage ? (
          <button
            type="button"
            data-testid="activity-load-more"
            onClick={() => void query.fetchNextPage()}
            disabled={query.isFetchingNextPage}
            className="press min-h-11 w-full rounded-lg border border-hairline bg-paper-deep px-4 text-body-sm text-ink-1 transition-colors duration-quick hover:bg-paper-calm disabled:opacity-60"
          >
            {query.isFetchingNextPage ? "載入中…" : "載入更多"}
          </button>
        ) : null}
      </div>
    </ActivityStreamCard>
  );
}